import { seedingWaitLabel, seedingWaits } from "./deployProblems";
import { resolveCodeDraft } from "./pipelineCode";
import {
  inputEditedSinceOpen,
  isUntitledScriptId,
  type StagedResource,
  type StageTabId,
  type StageTabRole,
  schemaIdOfInstance,
  unattachedSchemaEditorId,
} from "./stagingModel";

// Where the AI Helper's Insert lands (TTTL-217). The fence type decides
// which KIND of editor; the Studio's live state decides which row or slot
// of that kind — the AI may name the row, the Studio checks the name
// against what is staged and falls back to the selection. Pure: the
// hook (useApplyAiCode) reads the state, this module makes the call, so
// every rule has a unit test.

export type InsertKind = "script" | "schema" | "input" | "tests" | "pipeline";

export const INSERT_KINDS: ReadonlySet<string> = new Set<InsertKind>([
  "script",
  "schema",
  "input",
  "tests",
  "pipeline",
]);

/** The parsed fence info string: ```lang:artifact:name. */
export interface InsertFence {
  lang: string;
  artifact: string | null;
  name: string | null;
}

/** The slice of Studio state the routing reads. */
export interface InsertState {
  stageTab: StageTabId;
  editorKind: "input" | "script" | "schema";
  effectiveSelectedId: string | null;
  transforms: readonly StagedResource[];
  schemas: readonly StagedResource[];
  scriptDrafts: Readonly<Record<string, string>>;
  schemaDrafts: Readonly<Record<string, string>>;
  schemaDocument: string;
  schemaOrigin: string | null;
  inputDocument: string;
  inputOrigin: string | null;
  savedInput: string;
  pipelineCodeDraft: { text: string; basedOn: string } | null;
  generatedCode: string;
}

export type InsertTarget =
  /** A staged transform row (named by the fence, or the Transform tab's
   *  own selection); rowId null = a fresh untitled script. */
  | { kind: "script"; tab: "transform"; rowId: string | null; rowName: string }
  /** A SCHEMA slot: the open schema editor's own row (`open`), or the
   *  tab's unattached block. */
  | {
      kind: "schema";
      tab: StageTabRole;
      slot: string;
      open: boolean;
      slotName: string;
    }
  | { kind: "input"; tab: "source" }
  | { kind: "tests"; tab: "tests" }
  | { kind: "pipeline" };

export function insertKindOf(fence: InsertFence): InsertKind | null {
  return fence.artifact !== null && INSERT_KINDS.has(fence.artifact)
    ? (fence.artifact as InsertKind)
    : null;
}

const RHAI = /\.rhai$/i;

// A fence's name matches a staged row with or without the extension, any
// case — the AI writes "mask_pii" as readily as "mask_pii.rhai".
function scriptRowNamed(
  name: string | null,
  transforms: readonly StagedResource[],
): StagedResource | null {
  if (name === null) return null;
  const wanted = name.replace(RHAI, "").toLowerCase();
  return (
    transforms.find((t) => t.name.replace(RHAI, "").toLowerCase() === wanted) ??
    null
  );
}

function scriptTarget(fence: InsertFence, state: InsertState): InsertTarget {
  const named = scriptRowNamed(fence.name, state.transforms);
  if (named) {
    return {
      kind: "script",
      tab: "transform",
      rowId: named.id,
      rowName: named.name,
    };
  }
  // The Transform tab's selected row is the user's own aim; from another
  // tab there is no visible selection to honor, so a fresh script.
  const selected = state.effectiveSelectedId;
  if (state.stageTab === "transform" && selected !== null) {
    const row = state.transforms.find((t) => t.id === selected);
    if (row) {
      return {
        kind: "script",
        tab: "transform",
        rowId: row.id,
        rowName: row.name,
      };
    }
    if (isUntitledScriptId(selected)) {
      return {
        kind: "script",
        tab: "transform",
        rowId: selected,
        rowName: "Untitled script",
      };
    }
  }
  return { kind: "script", tab: "transform", rowId: null, rowName: "" };
}

const TAB_TITLES: Record<StageTabRole, string> = {
  source: "Source",
  transform: "Transform",
  sink: "Sink",
};

// The schema editor open on a stage tab, as the selection id of its row
// (a staged schema's instance, or a slot pseudo-row); null when the tab
// shows another editor. The Tests tab has no schema editor.
function openSchemaRow(state: InsertState): string | null {
  if (state.stageTab === "tests" || state.editorKind !== "schema") return null;
  return state.effectiveSelectedId;
}

function schemaTarget(state: InsertState): InsertTarget {
  const tab: StageTabRole =
    state.stageTab === "tests" ? "sink" : state.stageTab;
  const tabSlotName = `${TAB_TITLES[tab]} schema`;
  // The schema editor already open on this tab takes the insert as a
  // keystroke would.
  const open = openSchemaRow(state);
  if (open !== null) {
    const stagedId = schemaIdOfInstance(open) ?? open;
    const staged = state.schemas.find((s) => s.id === stagedId);
    return {
      kind: "schema",
      tab,
      slot: open,
      open: true,
      slotName: staged?.name ?? tabSlotName,
    };
  }
  return {
    kind: "schema",
    tab,
    slot: unattachedSchemaEditorId(tab),
    open: false,
    slotName: tabSlotName,
  };
}

export function insertTarget(
  fence: InsertFence,
  state: InsertState,
): InsertTarget | null {
  switch (insertKindOf(fence)) {
    case "script":
      return scriptTarget(fence, state);
    case "schema":
      return schemaTarget(state);
    case "input":
      return { kind: "input", tab: "source" };
    case "tests":
      return { kind: "tests", tab: "tests" };
    case "pipeline":
      return { kind: "pipeline" };
    default:
      return null;
  }
}

/** Where the insert lands, for the Replace confirm ("Inserting into …"). */
export function insertDestination(target: InsertTarget): string {
  switch (target.kind) {
    case "script":
      return target.rowId === null ? "a new script" : target.rowName;
    case "schema":
      return target.slotName;
    case "input":
      return "the Source input";
    case "tests":
      return "the Tests";
    case "pipeline":
      return "the pipeline";
  }
}

// Draft text that isn't saved anywhere: the difference between what the
// editor holds and its origin, "" when nothing would be lost.
function unsavedScript(
  target: InsertTarget & { kind: "script" },
  state: InsertState,
): string {
  return target.rowId === null ? "" : (state.scriptDrafts[target.rowId] ?? "");
}

function unsavedSchema(
  target: InsertTarget & { kind: "schema" },
  state: InsertState,
): string {
  if (!target.open) return state.schemaDrafts[target.slot] ?? "";
  return state.schemaDocument === (state.schemaOrigin ?? "")
    ? ""
    : state.schemaDocument;
}

function unsavedInput(state: InsertState): string {
  return inputEditedSinceOpen(
    state.inputDocument,
    state.inputOrigin,
    state.savedInput,
  )
    ? state.inputDocument
    : "";
}

function unsavedPipelineCode(state: InsertState): string {
  const text = resolveCodeDraft(state.pipelineCodeDraft, state.generatedCode);
  return text === state.generatedCode ? "" : text;
}

function unsavedWork(
  target: InsertTarget,
  state: InsertState,
): { text: string; what: string } {
  switch (target.kind) {
    case "script":
      return {
        text: unsavedScript(target, state),
        what: `the unsaved edits of ${target.rowName}`,
      };
    case "schema":
      return {
        text: unsavedSchema(target, state),
        what: "your unsaved schema draft",
      };
    case "input":
      return { text: unsavedInput(state), what: "your edited input document" };
    case "pipeline":
      return {
        text: unsavedPipelineCode(state),
        what: "your unsaved pipeline code edits",
      };
    case "tests":
      // Inserted cases get free names — nothing is overwritten.
      return { text: "", what: "" };
  }
}

// Unsaved work the insert would overwrite, named for the confirm — null
// when nothing is lost: an empty editor, saved content, or the same text.
export function insertReplaces(
  target: InsertTarget,
  state: InsertState,
  code: string,
): string | null {
  const { text, what } = unsavedWork(target, state);
  return text !== "" && text !== code ? what : null;
}

// "name", "name 2", … so an inserted batch never overwrites a staged case.
export function withFreeTestNames<T extends { name: string }>(
  cases: T[],
  takenNames: Iterable<string>,
): T[] {
  const taken = new Set(takenNames);
  return cases.map((c) => {
    let name = c.name;
    for (let i = 2; taken.has(name); i++) name = `${c.name} ${i}`;
    taken.add(name);
    return { ...c, name };
  });
}

/** The wait a pipeline landing must respect, or null: a dropped stage's
 *  schema seed still resolving (schemaSeeding). The landing pins its code
 *  draft on the generated code and switches to Full; a seed landing after
 *  that regenerates the code and the draft is discarded (resolveCodeDraft)
 *  — so the landing waits, as the toolbar's and the AI's view switches do.
 *  Other landings write into tabs the seed never touches. */
export function pipelineLandingWait(
  target: InsertTarget,
  seedingStageIds: readonly string[],
  stages: { sources: StagedResource[]; sinks: StagedResource[] },
): string | null {
  if (target.kind !== "pipeline") return null;
  const wait = seedingWaits(seedingStageIds, stages)[0];
  return wait ? seedingWaitLabel(wait) : null;
}
